/* ══════════════════════════════════════════════════════════════
   Performance Chart — 30-Day Rolling Accuracy + Sharpe
   Canvas-based mini chart for strategy performance
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from '../services/api-client';
import { renderUnavailableBlock } from './data-state';

export interface PerformanceData {
  rolling30d: { accuracy: number; sharpe: number; winRate: number; totalTrades: number };
  rolling90d: { accuracy: number; sharpe: number; winRate: number; totalTrades: number };
  dailyAccuracy: number[];
  modelHealth: string;
  lastRetrained: string;
  driftScore: number;
}

function miniSparkline(values: number[], width: number, height: number, color: string): string {
  if (values.length < 2) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);

  const points = values.map((v, i) => `${i * step},${height - ((v - min) / range * height)}`).join(' ');
  return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <polyline points="${points}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

function renderWithData(container: HTMLElement, d: PerformanceData): void {
  const healthColor = d.modelHealth === 'healthy' ? 'var(--india-green)' : d.modelHealth === 'warning' ? 'var(--india-saffron)' : 'var(--india-red)';
  const healthEmoji = d.modelHealth === 'healthy' ? '🟢' : d.modelHealth === 'warning' ? '🟡' : '🔴';

  container.innerHTML = `
    <div class="glass-card performance-chart-panel">
      <div class="card-header">
        <h3>📈 Model Performance</h3>
        <span class="health-badge" style="color:${healthColor}">${healthEmoji} ${d.modelHealth}</span>
      </div>

      <div class="perf-sparkline">
        <span class="spark-label">30-Day Accuracy Trend</span>
        ${miniSparkline(d.dailyAccuracy, 280, 60, 'var(--india-green)')}
      </div>

      <div class="perf-metrics-grid">
        <div class="perf-period">
          <h4>30-Day Rolling</h4>
          <div class="perf-stat"><span>Accuracy</span><span class="perf-val">${d.rolling30d.accuracy}%</span></div>
          <div class="perf-stat"><span>Sharpe</span><span class="perf-val">${d.rolling30d.sharpe}</span></div>
          <div class="perf-stat"><span>Win Rate</span><span class="perf-val">${d.rolling30d.winRate}%</span></div>
          <div class="perf-stat"><span>Trades</span><span class="perf-val">${d.rolling30d.totalTrades}</span></div>
        </div>
        <div class="perf-period">
          <h4>90-Day Rolling</h4>
          <div class="perf-stat"><span>Accuracy</span><span class="perf-val">${d.rolling90d.accuracy}%</span></div>
          <div class="perf-stat"><span>Sharpe</span><span class="perf-val">${d.rolling90d.sharpe}</span></div>
          <div class="perf-stat"><span>Win Rate</span><span class="perf-val">${d.rolling90d.winRate}%</span></div>
          <div class="perf-stat"><span>Trades</span><span class="perf-val">${d.rolling90d.totalTrades}</span></div>
        </div>
      </div>

      <div class="perf-footer">
        <small>Last updated: ${d.lastRetrained} · Drift: ${d.driftScore}</small>
      </div>
    </div>`;
}

export function renderPerformanceChart(container: HTMLElement, data?: PerformanceData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div class="glass-card performance-chart-panel" style="padding:2rem;text-align:center;">Loading performance...</div>`;
    apiFetch<PerformanceData>('/ml/performance', { cacheTTL: 300 }).then(result => {
      if (result.data && result.data.rolling30d) {
        renderWithData(container, result.data);
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card performance-chart-panel', 'Live performance unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card performance-chart-panel', 'Live performance unavailable');
    });
  }
}
